export const heroCapabilities = [
  "ICP Scoring",
  "Buyer Signal Detection",
  "Stakeholder Intelligence",
  "MEDDICC Review",
  "Technical Validation",
  "AI GTM Coaching",
];

export const signalToExecutionWorkflow = [
  "Buyer Signal",
  "Account Brief",
  "Stakeholder Map",
  "MEDDICC Gaps",
  "Technical Validation Plan",
  "Next-Best Action",
  "Manager Review",
  "CS Handoff",
];

export const problemPoints = [
  "Teams chase activity before they verify ICP fit, urgency, or buying signals.",
  "Signals live across CRM, notes, meetings, and tools but do not trigger consistent action.",
  "Forecasts depend on rep confidence instead of verified signal, stakeholder, and MEDDICC evidence.",
  "Champions are assumed instead of tested across the real buying group.",
  "Managers cannot inspect every opportunity deeply enough to coach execution early.",
  "Founders struggle to scale what works in founder-led sales without losing context or discipline.",
];

export const principles = [
  {
    title: "Evidence Over Opinion",
    description:
      "Prioritisation, qualification, and forecast calls should rely on observable signal and execution evidence, not hopeful updates.",
  },
  {
    title: "Signals Need Action",
    description:
      "Intent, account movement, meeting context, and stakeholder shifts should create clear next steps instead of sitting inside disconnected tools.",
  },
  {
    title: "MEDDICC in Motion",
    description:
      "Methodology should guide daily GTM execution, not sit inside static CRM fields waiting for end-of-quarter cleanup.",
  },
  {
    title: "Champions Must Be Tested",
    description:
      "A friendly contact is not a champion until they show influence, urgency, access, and willingness to help the deal move.",
  },
  {
    title: "Every Meeting Should Create Action",
    description:
      "Customer conversations should generate account context, stakeholder movement, risk visibility, and next-best actions.",
  },
  {
    title: "One GTM View",
    description:
      "Sales, RevOps, founders, SEs, CS, and leadership should learn from the same account, opportunity, and workflow evidence.",
  },
];

export const workflowColumns = [
  {
    title: "Signals & GTM Inputs",
    items: [
      "ICP configuration",
      "Leads and accounts",
      "CRM fields",
      "Meeting notes",
      "Call transcripts",
      "Product or intent signals",
      "Stakeholder data",
    ],
  },
  {
    title: "AI Prioritisation",
    items: [
      "ICP fit",
      "Buying triggers",
      "Lead/account score",
      "Deal risk",
      "Champion strength",
      "Stakeholder coverage",
      "Forecast confidence",
    ],
  },
  {
    title: "Execution Guidance",
    items: [
      "Ask Coach",
      "Account brief",
      "Outreach angle",
      "MEDDICC gaps",
      "Deal review",
      "Action plan",
      "Follow-up guidance",
    ],
  },
  {
    title: "Leadership Visibility",
    items: [
      "Pipeline quality",
      "Signal-to-action gaps",
      "Forecast risk",
      "Handoff leakage",
      "Manager coaching gaps",
      "Team execution patterns",
    ],
  },
];

export const signalCapabilities = [
  {
    title: "Signal-to-Opportunity Workflow",
    description:
      "Detect account signals, score ICP fit, enrich context, recommend action, and move qualified opportunities into execution.",
  },
  {
    title: "GTM Leakage Analysis",
    description:
      "Identify where leads, handoffs, deal context, stakeholder coverage, or next steps are falling through the cracks.",
  },
  {
    title: "AI Account Briefs",
    description:
      "Turn CRM data, notes, call transcripts, ICP fields, and account signals into concise briefs for reps and managers.",
  },
  {
    title: "Experiment & Playbook Feedback Loops",
    description:
      "Track which segments, messages, signals, and plays create real pipeline instead of just more activity.",
  },
  {
    title: "ICP & Lead Scoring",
    description:
      "Score leads and accounts against ICP fit, persona relevance, urgency, and buying triggers so teams can prioritise with more precision.",
  },
  {
    title: "Stakeholder & Champion Analysis",
    description:
      "Map influence, identify relationship gaps, and separate real champions from friendly contacts before execution slips.",
  },
];

export const executionCapabilities = [
  {
    title: "MEDDICC Deal Review",
    description:
      "Identify missing metrics, weak economic buyer access, unclear decision process, technical validation gaps, and champion risk before forecast judgment hardens.",
  },
  {
    title: "Ask Coach",
    description:
      "Give sellers, founders, managers, and customer-facing teams an AI coach that challenges assumptions and recommends next-best actions with humans still in control.",
  },
  {
    title: "Value Pyramid Account Strategy",
    description:
      "Build account plans tied to business priorities, pains, initiatives, risks, expansion opportunities, and cross-functional execution plans.",
  },
  {
    title: "Conversation Intelligence",
    description:
      "Turn meeting notes and transcripts into MEDDICC updates, technical discovery gaps, risks, follow-ups, and coaching moments.",
  },
  {
    title: "Execution Tasks",
    description:
      "Convert account strategy into weekly actions, owners, due dates, AE/SE/CS handoffs, and next-step commitments.",
  },
  {
    title: "CRO Pipeline View",
    description:
      "Show which accounts and deals are real, which are risky, and which need leadership attention across the signal-to-execution workflow.",
  },
];

export const presalesExecutionCards = [
  {
    title: "Demo planning",
    description:
      "Align discovery findings, buyer priorities, and walkthrough objectives before the customer meeting starts.",
  },
  {
    title: "Technical discovery gaps",
    description:
      "Surface the missing technical questions, blockers, and proof points that need validation before momentum slips.",
  },
  {
    title: "Proof-of-value success criteria",
    description:
      "Make value, evaluation criteria, and success measures visible for both commercial and technical stakeholders.",
  },
  {
    title: "Technical stakeholder mapping",
    description:
      "Track who influences architecture, validation, security, and implementation decisions across the buying group.",
  },
  {
    title: "AE/SE handoff context",
    description:
      "Keep discovery context, account priorities, and next-step ownership aligned across Sales and PreSales.",
  },
  {
    title: "Post-demo next-best actions",
    description:
      "Turn meeting outcomes into follow-ups, technical validations, stakeholder moves, and shared execution plans.",
  },
];

export const revenueQuestions = [
  "Which accounts are showing real buying signals?",
  "Which opportunities are real versus hopeful?",
  "Which stakeholders are missing?",
  "Where is MEDDICC weak?",
  "Where does Sales need PreSales support?",
  "What technical validation is required?",
  "What should the team do next?",
];

export const credibilityCards = [
  {
    title: "Enterprise GTM experience",
    description:
      "Built from practical experience across enterprise sales engineering, account strategy, MEDDICC, and customer-facing execution.",
  },
  {
    title: "Human-in-the-loop execution",
    description:
      "AI identifies, summarises, and recommends. Sellers, SEs, managers, founders, and CS leaders decide and act.",
  },
  {
    title: "Sales + PreSales + CS alignment",
    description:
      "Designed to connect discovery, technical validation, stakeholder context, next actions, and customer handoff.",
  },
  {
    title: "Evidence over opinion",
    description:
      "Built to make signals, gaps, risks, and next steps visible before forecast confidence hardens.",
  },
];

export const differentiationCards = [
  {
    title: "Beyond conversation analysis",
    description:
      "Conversations matter, but the system also needs account signals, stakeholder movement, ICP fit, technical validation, and next-step ownership.",
  },
  {
    title: "Beyond forecast inspection",
    description:
      "Forecast risk is an output. BestSalesCoach.ai helps teams inspect the evidence and actions that create or reduce that risk.",
  },
  {
    title: "Beyond activity automation",
    description:
      "More activity is not the same as better execution. BestSalesCoach.ai helps teams decide which accounts, stakeholders, and actions matter.",
  },
  {
    title: "Built for complex tech sales",
    description:
      "Designed for Sales, PreSales, RevOps, CS, and founder-led GTM teams selling technical products into multi-stakeholder buying groups.",
  },
];

export const croBullets = [
  "Prioritise accounts using ICP fit and buying signals.",
  "Detect weak MEDDICC evidence.",
  "Identify stalled, single-threaded, or poorly sponsored deals.",
  "Surface signal-to-action and handoff leakage.",
  "Give managers deal coaching insights faster.",
  "Reduce pipeline noise and false confidence.",
];

export const founderBullets = [
  "Validate ICP and target personas.",
  "Score early leads and accounts.",
  "Prepare for discovery and demo calls.",
  "Avoid wasting time on poor-fit opportunities.",
  "Build champions in complex accounts.",
  "Turn founder intuition into repeatable process.",
  "Create investor-ready visibility into pipeline quality.",
];

export const valueTeamBullets = [
  "Collaboration across Sales, SE, CS, RevOps, and leadership",
  "Accountability through clear next actions",
  "Champion building through stakeholder intelligence",
  "Customer value through business-impact selling",
  "Execution discipline through continuous review and follow-through",
];

export const valueCreationBreakdowns = [
  "Teams rely on CRM updates without inspecting signal quality or deal evidence.",
  "Sales, SE, CS, and leadership operate from different versions of the account and opportunity.",
  "Champions are not built early enough or tested deeply enough.",
  "Follow-ups are missed or fail to create customer commitment.",
  "Account plans are created once and then forgotten as context shifts.",
  "Managers spend too much time chasing updates instead of coaching execution.",
];

export const valueCreationMakes = [
  "Collaboration",
  "Accountability",
  "Champion Building",
  "Customer Value",
  "Execution Discipline",
];

export const valueCreationOperationalises = [
  "Reviews opportunity quality",
  "Highlights missing MEDDICC evidence",
  "Creates account strategy through the Value Pyramid",
  "Recommends next actions",
  "Identifies stakeholder and champion gaps",
  "Helps teams align around customer outcomes",
];

export const signalUseCases = [
  {
    title: "Account Prioritisation",
    pain:
      "Teams chase activity instead of accounts showing real fit, urgency, or intent.",
    action:
      "Scores accounts using ICP fit, account signals, persona relevance, urgency, and available context.",
    outcome: "Reps focus on accounts with stronger fit and clearer buying evidence.",
  },
  {
    title: "Signal-to-Action Workflow",
    pain:
      "Buying signals live across tools, notes, calls, and CRM fields but do not trigger consistent action.",
    action:
      "Turns signals into account briefs, recommended actions, stakeholder hypotheses, and follow-up guidance.",
    outcome: "Faster response to meaningful account movement.",
  },
  {
    title: "Handoff Leakage",
    pain:
      "Context gets lost between founder, SDR, AE, SE, manager, and customer success.",
    action:
      "Creates one execution view across account strategy, opportunity health, stakeholder coverage, and next actions.",
    outcome: "Cleaner handoffs and stronger customer-facing execution.",
  },
  {
    title: "Playbook Learning",
    pain:
      "Teams do not know which segments, messages, signals, or plays are creating real pipeline.",
    action:
      "Captures execution patterns and converts learning into repeatable guidance.",
    outcome: "A GTM motion that compounds learning instead of relying on tribal knowledge.",
  },
];

export const useCaseGroups = [
  {
    title: "For CROs",
    cards: [
      {
        title: "Forecast Confidence",
        pain: "Committed deals still rely on rep judgment instead of buyer, signal, and stakeholder evidence.",
        action:
          "BestSalesCoach.ai surfaces MEDDICC gaps, handoff leakage, stakeholder risk, and next-step proof before deals hit the number.",
        outcome: "Cleaner commits, fewer surprises, and tighter forecast calls.",
      },
      {
        title: "Pipeline Quality",
        pain: "Pipeline coverage looks fine until inspection reveals weak execution and thin account context.",
        action:
          "The platform scores opportunity quality and flags accounts with incomplete signals, poor qualification, or weak sponsorship.",
        outcome: "More realistic pipeline, better prioritisation, and fewer false positives.",
      },
      {
        title: "Manager Coaching",
        pain: "Frontline managers spend too much time collecting updates and not enough time coaching.",
        action:
          "AI reviews highlight which signals matter, where action is missing, and which opportunities need leadership attention.",
        outcome: "Faster coaching loops and better inspection quality across the team.",
      },
      {
        title: "Methodology Adoption",
        pain: "MEDDICC often becomes a checkbox exercise instead of an execution discipline.",
        action:
          "BestSalesCoach.ai turns methodology into active guidance inside account briefs, reviews, follow-ups, and opportunity planning.",
        outcome: "More consistent execution and stronger win discipline.",
      },
    ],
  },
  {
    title: "For Founders",
    cards: [
      {
        title: "Founder-Led Sales",
        pain: "Founder intuition drives deals, but the motion is hard to scale beyond the founder.",
        action:
          "The platform captures what good targeting, qualification, and account progression look like and turns it into repeatable AI-guided execution.",
        outcome: "A GTM motion that can be coached, measured, and delegated.",
      },
      {
        title: "ICP Validation",
        pain: "Early teams waste cycles on accounts that will never buy.",
        action:
          "BestSalesCoach.ai scores fit, persona relevance, urgency, and account signals to sharpen targeting.",
        outcome: "Better conversion efficiency and tighter learning loops.",
      },
      {
        title: "Enterprise Deal Navigation",
        pain: "Complex deals stall when stakeholder mapping and buying process are incomplete.",
        action:
          "AI analysis identifies missing decision-makers, weak champions, and execution gaps before they become losses.",
        outcome: "Better visibility into enterprise deal risks and required actions.",
      },
      {
        title: "Repeatable GTM Motion",
        pain: "Signals from calls, notes, and deals are scattered across tools and people.",
        action:
          "The platform turns conversations and account data into action plans, coaching outputs, and repeatable workflows.",
        outcome: "A GTM system that compounds learning instead of losing it.",
      },
    ],
  },
  {
    title: "For GTM Teams",
    cards: [
      {
        title: "Deal Acceleration",
        pain: "Opportunities drift because risks and next steps are not surfaced early enough.",
        action:
          "BestSalesCoach.ai identifies blockers, prioritises next actions, and keeps account momentum visible across the team.",
        outcome: "Clearer progression signals and fewer unmanaged next-step gaps.",
      },
      {
        title: "Champion Building",
        pain: "Teams confuse friendly contacts with real internal advocates.",
        action:
          "The platform evaluates influence, access, urgency, and relationship depth to test champion strength.",
        outcome: "Stronger internal sponsorship and better multi-threading.",
      },
      {
        title: "Expansion Strategy",
        pain: "Expansion planning often lacks a clear view of pain, priorities, and executive value.",
        action:
          "Value Pyramid strategy ties stakeholders, business impact, and risks into a practical account plan.",
        outcome: "Clearer expansion timing and stronger strategic account planning.",
      },
      {
        title: "Cross-Functional Alignment",
        pain: "Sales, SE, CS, and leadership operate from different versions of the account.",
        action:
          "BestSalesCoach.ai creates one view of account strategy, opportunity health, signal history, and execution priorities.",
        outcome: "Tighter team alignment and better customer-facing execution.",
      },
    ],
  },
];

export const serviceEngagements = [
  {
    title: "AI GTM Diagnostic",
    duration: "1 week",
    description:
      "Map your ICP, GTM tools, workflows, and leakage points. Identify where AI can improve execution and where the playbook needs tightening first.",
    deliverables: [
      "GTM workflow map",
      "Signal and leakage analysis",
      "AI workflow opportunities",
      "30-day execution roadmap",
    ],
  },
  {
    title: "Signal-to-Opportunity Sprint",
    duration: "2–4 weeks",
    description:
      "Build one practical workflow from signal detection to enrichment, scoring, stakeholder mapping, and next-best action.",
    deliverables: [
      "ICP scoring logic",
      "Account/lead brief",
      "Recommended actions",
      "Human-in-the-loop review path",
    ],
  },
  {
    title: "Pipeline Execution Sprint",
    duration: "2–4 weeks",
    description:
      "Improve MEDDICC quality, stakeholder coverage, champion strength, forecast confidence, and manager coaching visibility.",
    deliverables: [
      "Deal review workflow",
      "MEDDICC gap analysis",
      "Stakeholder/champion review",
      "Coaching recommendations",
    ],
  },
  {
    title: "Fractional AI GTM Engineer",
    duration: "Monthly",
    description:
      "Ongoing GTM Engineering support to improve signal workflows, GTM experiments, pipeline execution, and revenue visibility.",
    deliverables: [
      "Weekly workflow improvements",
      "GTM experiment support",
      "Execution analytics",
      "CRO/founder review sessions",
    ],
  },
];
